import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../../colorVars';
import { addExitTactic } from '../../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/addExitTactic';
import { useWebsocketStore } from '../../../../../../../../../../stores/websocketStore/websocketProvider';
import TextButton from '../../../../../../../../../atomic/buttons/textButton/TextButton';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { TableSelect } from '../../../../../../../../../atomic/inputs/tableSelect/TableSelect';
import { Body3, fontSizes } from '../../../../../../../../../atomic/text/Text';
import {
  atrTrailValidation,
} from '../../../../../../../../../forms/tradeplans/AddTradePlan/DataValidation';
import { IndividualPanelHeader } from '../../../../individualTacticPanels/IndividualPanelHeader';
import style from './IndividualAddTacticPanel.module.css';

export const AddAtrTrailTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, softOrderId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const [headerText, setHeaderText] = useState('Add ATR Trail');
    const [headerTextColor, setHeaderTextColor] = useState(colors.lightGrey);

    const [details, setDetails] = useState({
      pctOfAtr: 100,
      atr: {
        atrType: 'WILDER',
        candlesBack: 14,
        candleDuration: 1,
        timeUnit: 'MINUTES',
      },
    });
    const timeoutRef = useRef(null);

    const addTactic = () => {
      const detailsCopy = cloneDeep(details);
      detailsCopy.pctOfAtr = details.pctOfAtr / 100;
      const validationResult = atrTrailValidation(detailsCopy);
      if (validationResult[0] === false) {
        setHeaderText(validationResult[1]);
        setHeaderTextColor(colors.red);
        const timer = setTimeout(() => {
          setHeaderText('Add ATR Trail');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      } else {
        addExitTactic(tradePlanId, {
          tactic: 'ATR_TRAIL',
          details: detailsCopy,
        });
        setHeaderText('ATR Trail Added');
        setHeaderTextColor(colors.green);
        const timer = setTimeout(() => {
          setHeaderText('Add ATR Trail');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      }
    };
    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);

    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <IndividualPanelHeader />
            <tbody className={style.tbody}>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Pct. of ATR
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <EditableTableFieldNoOnBlurUpdate
                      inputType="number"
                      value={details.pctOfAtr}
                      setValue={(value) =>
                        setDetails((prev) => ({ ...prev, pctOfAtr: value }))
                      }
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      color={colors.lightGrey}
                    />
                    <Body3
                      margin="3px 0 0 0"
                      fontWeight={700}
                      color={colors.lightGrey}
                    >
                      %
                    </Body3>
                  </div>
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    ATR Type
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <TableSelect
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      onChange={(value) =>
                        setDetails((prev) => ({
                          ...prev,
                          atr: { ...prev.atr, atrType: value },
                        }))
                      }
                      value={details.atr.atrType}
                      options={[
                        { label: 'Wilder', value: 'WILDER' },
                        { label: 'SMA', value: 'SMA' },
                        { label: 'SMA No Gap', value: 'SMA_NO_GAP' },
                      ]}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Candle Duration
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <EditableTableFieldNoOnBlurUpdate
                      inputType="number"
                      value={details.atr.candleDuration}
                      setValue={(value) =>
                        setDetails((prev) => ({
                          ...prev,
                          atr: { ...prev.atr, candleDuration: value },
                        }))
                      }
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      color={colors.lightGrey}
                    />
                  </div>
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Time Unit
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <TableSelect
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      onChange={(value) =>
                        setDetails((prev) => ({
                          ...prev,
                          atr: { ...prev.atr, timeUnit: value },
                        }))
                      }
                      value={details.atr.timeUnit}
                      options={[
                        { label: 'Minute', value: 'MINUTES' },
                        { label: 'Day', value: 'DAYS' },
                        { label: 'Week', value: 'WEEKS' },
                        { label: 'Month', value: 'MONTHS' },
                        { label: 'Quarter', value: 'QUARTERS' },
                        { label: 'Year', value: 'YEARS' },
                      ]}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Candles Back
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <Body3
                      margin="3px 0 0 0"
                      fontWeight={700}
                      color={colors.lightGrey}
                    >
                      #
                    </Body3>
                    <EditableTableFieldNoOnBlurUpdate
                      inputType="number"
                      value={details.atr.candlesBack}
                      setValue={(value) =>
                        setDetails((prev) => ({
                          ...prev,
                          atr: { ...prev.atr, candlesBack: value },
                        }))
                      }
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      color={colors.lightGrey}
                    />
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <header className={style.header}>
            <Body3 color={headerTextColor} fontWeight={700}>
              {headerText}
            </Body3>
            <TextButton
              disabled={tradePlan.exitTactics['ATR_TRAIL']}
              color={colors.lightGrey}
              backgroundColor={
                tradePlan.exitTactics['ATR_TRAIL'] ? colors.grey : colors.blue
              }
              fontSize="small"
              clickHandler={addTactic}
              width="20%"
              height="50%"
            >
              Add
            </TextButton>
          </header>
        </div>
      </TabPanel>
    );
  }
);

AddAtrTrailTacticPanel.tabsRole = 'TabPanel';
